import { z } from 'zod';
import { CONTENT_STATUSES, NAV_LOCATIONS, SECTION_TYPES, SETTING_GROUPS } from '../constants.js';
import {
  emailSchema,
  linkSchema,
  optionalText,
  requiredText,
  seoSchema,
  slugSchema,
  sortOrderSchema,
} from './common.js';

/**
 * Website content — everything an Editor can change without a deploy.
 *
 * The rule these all share: nothing is hard-coded in the React app that a person could
 * reasonably want to change. Headings, ordering, which sections appear, the navigation,
 * the footer, the phone number in three places — all of it is a row.
 */

const statusSchema = z.enum(CONTENT_STATUSES).default('DRAFT');

/* -------------------------------------------------------------------------- */
/* Pages and their sections                                                    */
/* -------------------------------------------------------------------------- */

export const pageSchema = z
  .object({
    slug: slugSchema,
    title: requiredText(160, 'Title'),
    status: statusSchema,
    ...seoSchema.shape,
  })
  .strict();
export type PageInput = z.infer<typeof pageSchema>;

export const sectionSchema = z.object({
  type: z.enum(SECTION_TYPES),
  payload: z.unknown(),
  isEnabled: z.boolean().default(true),
  sortOrder: sortOrderSchema,
  /**
   * An internal name for the section list in the editor. Never rendered publicly —
   * without it a page of six `product_grid`s is six identical rows to drag around.
   */
  adminLabel: optionalText(80),
});
export type SectionInput = z.infer<typeof sectionSchema>;

/** Reordering posts the whole list at once, so the result cannot have two of one position. */
export const reorderSchema = z.object({
  ids: z.array(z.coerce.number().int().positive()).min(1).max(500),
});

/* -------------------------------------------------------------------------- */
/* Projects — the portfolio                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A project (case study).
 *
 * `isFeatured` is what the home page reads. **At most one project may carry it**, held
 * by a partial unique index in Postgres and by a transaction in the service that clears
 * the previous holder before setting the new one — see TECHNICAL_DOCUMENT.md §5.2. The
 * constraint is in the database because the rule is a property of the data, and an
 * application that forgets it once leaves a home page rendering two heroes.
 */
export const projectSchema = z
  .object({
    slug: slugSchema,
    title: requiredText(200, 'Title'),
    client: optionalText(160),
    summary: requiredText(600, 'Summary'),
    body: optionalText(40_000),
    coverImageUrl: optionalText(500),
    coverImageAlt: optionalText(200),
    gallery: z
      .array(z.object({ url: z.string().trim().min(1).max(500), alt: optionalText(200) }))
      .max(24)
      .default([]),
    /** Slugs from `services`. Drives the filter on the projects index. */
    services: z.array(z.string().trim().max(120)).max(12).default([]),
    technologies: z.array(requiredText(60, 'Technology')).max(40).default([]),
    /** Result lines rendered as ticked bullets on the case study. */
    outcomes: z.array(requiredText(200, 'Outcome')).max(12).default([]),
    year: z.coerce.number().int().min(1990).max(2100).optional().nullable(),
    projectUrl: linkSchema.optional().nullable(),
    isFeatured: z.boolean().default(false),
    status: statusSchema,
    sortOrder: sortOrderSchema,
    ...seoSchema.shape,
  })
  .strict();
export type ProjectInput = z.infer<typeof projectSchema>;

export const projectListQuerySchema = z.object({
  search: optionalText(120),
  service: optionalText(120),
  status: z.enum(CONTENT_STATUSES).optional(),
  featured: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
});

/* -------------------------------------------------------------------------- */
/* Services                                                                    */
/* -------------------------------------------------------------------------- */

export const serviceSchema = z
  .object({
    slug: slugSchema,
    title: requiredText(160, 'Title'),
    summary: requiredText(600, 'Summary'),
    body: optionalText(40_000),
    /** A key from the icon set in `apps/web/src/components/Icon.tsx`, not arbitrary SVG. */
    icon: optionalText(60),
    imageUrl: optionalText(500),
    bullets: z.array(requiredText(200, 'Point')).max(12).default([]),
    status: statusSchema,
    sortOrder: sortOrderSchema,
    ...seoSchema.shape,
  })
  .strict();
export type ServiceInput = z.infer<typeof serviceSchema>;

/* -------------------------------------------------------------------------- */
/* People, quotes, history                                                     */
/* -------------------------------------------------------------------------- */

export const teamMemberSchema = z
  .object({
    name: requiredText(120, 'Name'),
    role: requiredText(120, 'Role'),
    bio: optionalText(2000),
    photoUrl: optionalText(500),
    email: emailSchema.optional().nullable(),
    linkedinUrl: linkSchema.optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: sortOrderSchema,
  })
  .strict();
export type TeamMemberInput = z.infer<typeof teamMemberSchema>;

export const testimonialSchema = z
  .object({
    quote: requiredText(2000, 'Quote'),
    authorName: requiredText(120, 'Author'),
    authorRole: optionalText(120),
    company: optionalText(160),
    photoUrl: optionalText(500),
    /**
     * Not editable in the back office and not sent to the site: nothing ever rendered
     * it. Kept here, and as a column, so the stored values survive and the field can be
     * restored without a migration.
     *
     * If it is restored: null rather than 5 when unknown — an invented rating is a
     * fabricated review.
     */
    rating: z.coerce.number().int().min(1).max(5).optional().nullable(),
    /** Optional link to the project this quote is about. */
    projectId: z.coerce.number().int().positive().optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: sortOrderSchema,
  })
  .strict();
export type TestimonialInput = z.infer<typeof testimonialSchema>;

export const milestoneSchema = z
  .object({
    year: z.coerce.number().int().min(1990).max(2100),
    title: requiredText(160, 'Title'),
    description: optionalText(600),
    isActive: z.boolean().default(true),
    sortOrder: sortOrderSchema,
  })
  .strict();
export type MilestoneInput = z.infer<typeof milestoneSchema>;

/* -------------------------------------------------------------------------- */
/* Blog                                                                        */
/* -------------------------------------------------------------------------- */

export const postSchema = z
  .object({
    slug: slugSchema,
    title: requiredText(200, 'Title'),
    excerpt: requiredText(500, 'Excerpt'),
    body: requiredText(80_000, 'Body'),
    coverImageUrl: optionalText(500),
    category: optionalText(80),
    authorName: optionalText(120),
    /**
     * When the post goes live. A future date with status PUBLISHED is scheduled: the
     * public list filters on `published_at <= now()`, so nothing needs to run at
     * midnight for it to appear.
     */
    publishedAt: z.coerce.date().optional().nullable(),
    status: statusSchema,
    ...seoSchema.shape,
  })
  .strict();
export type PostInput = z.infer<typeof postSchema>;

/* -------------------------------------------------------------------------- */
/* Navigation                                                                  */
/* -------------------------------------------------------------------------- */

export const navItemSchema = z
  .object({
    location: z.enum(NAV_LOCATIONS),
    label: requiredText(60, 'Label'),
    href: linkSchema,
    /** Null for a top-level item; an id makes this a child in the header dropdown. */
    parentId: z.coerce.number().int().positive().optional().nullable(),
    opensInNewTab: z.boolean().default(false),
    isEnabled: z.boolean().default(true),
    sortOrder: sortOrderSchema,
  })
  .strict();
export type NavItemInput = z.infer<typeof navItemSchema>;

/* -------------------------------------------------------------------------- */
/* Media                                                                       */
/* -------------------------------------------------------------------------- */

export const mediaUpdateSchema = z
  .object({
    alt: optionalText(200),
    title: optionalText(200),
  })
  .strict();

export const mediaListQuerySchema = z.object({
  search: optionalText(120),
  mimeGroup: z.enum(['image', 'document']).optional(),
});

/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Settings are a flat key → JSON store, written a batch at a time.
 *
 * A batch write rather than one PUT per key because the settings screen edits a whole
 * group at once: saving eight keys as eight requests means a half-applied brand block
 * if the sixth fails, and eight rows in the audit log for what the operator did once.
 */
export const settingsPatchSchema = z.object({
  values: z.record(z.string().min(1).max(120), z.unknown()),
});
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;

export const settingDefinitionSchema = z.object({
  key: z.string().min(1).max(120),
  group: z.enum(SETTING_GROUPS),
  label: requiredText(120, 'Label'),
  description: optionalText(400),
  /**
   * `password` is not merely a masked text box. A setting declared with this kind is a
   * SECRET: its value is never returned by the API, never written to an audit row, and
   * an empty submission means "leave it as it is" rather than "clear it". See
   * `services/settings.ts`.
   */
  kind: z.enum(['text', 'textarea', 'number', 'boolean', 'url', 'email', 'color', 'image', 'json', 'select', 'password']),
  options: z.array(z.object({ value: z.string(), label: z.string() })).max(50).optional(),
});
export type SettingDefinition = z.infer<typeof settingDefinitionSchema>;
